/* eslint-disable camelcase */
import React from "react"
import { useSelector } from "react-redux"

import {
    ARCHITECTURE_1oo1,
    ARCHITECTURE_1oo2,
    ARCHITECTURE_2oo2,
} from "const"
import { SilResultSubsystem } from "server/models"
import { LangType, StoreType, UserSettings } from "client/store"

import * as Const from "client/components/pdfmake/template/const"
import FormatPercentage, { FormatPercentageString } from "client/pages/sil/format/percentage"

import {
    ResultTable,
    ResultTableAnnotation,
    BlockContent,
} from "../../styled"


interface VdiVde21802019Props {
    subsystemList: readonly SilResultSubsystem[]
    showArchitectureConstraints: boolean
}

export const VdiVde21802019 = ({
    subsystemList,
    showArchitectureConstraints,
}: VdiVde21802019Props) => {
    const lang = useSelector((state: StoreType) => state.system.lang)
    return (
        <BlockContent>
            <ResultTable>
                <thead>
                    <tr>
                        <th>{lang.subsystem}</th>
                        <th>{lang.architecture}</th>
                        {
                            showArchitectureConstraints
                            && <th>{lang.architecturalConstraints}</th>
                        }
                        <th>{lang.ptc0A}</th>
                        <th>{lang.ptc1A}</th>
                        <th>{lang.ptc0B}</th>
                        <th>{lang.ptc1B}</th>
                        <th>{lang.ptc0C}</th>
                        <th>{lang.ptc1C}</th>
                        <th>{lang.beta}</th>
                        <th>{lang.t0}</th>
                        <th>{lang.t1}</th>
                        <th>{lang.t2}</th>
                    </tr>
                </thead>

                <tbody>
                    {
                        subsystemList.map((subsystem) => {
                            const ptc0A: JSX.Element = <FormatPercentage data={subsystem.vdivde21802019ptc0A} tableView />
                            const ptc1A: JSX.Element = <FormatPercentage data={subsystem.vdivde21802019ptc1A} tableView />
                            let ptc0B: string | JSX.Element = <FormatPercentage data={subsystem.vdivde21802019ptc0B} tableView />
                            let ptc1B: string | JSX.Element = <FormatPercentage data={subsystem.vdivde21802019ptc1B} tableView />
                            let ptc0C: string | JSX.Element = <FormatPercentage data={subsystem.vdivde21802019ptc0C} tableView />
                            let ptc1C: string | JSX.Element = <FormatPercentage data={subsystem.vdivde21802019ptc1C} tableView />
                            let beta: string | JSX.Element = <FormatPercentage data={subsystem.vdivde21802019beta} tableView />
                            const t0 = subsystem.vdivde21802019t0
                            const t1 = subsystem.vdivde21802019t1
                            const t2 = subsystem.vdivde21802019t2

                            if (subsystem.architecture === ARCHITECTURE_1oo1) {
                                ptc0B = lang.n_a
                                ptc1B = lang.n_a
                            }
                            if (
                                subsystem.architecture === ARCHITECTURE_1oo1
                                || subsystem.architecture === ARCHITECTURE_1oo2
                                || subsystem.architecture === ARCHITECTURE_2oo2
                            ) {
                                ptc0C = lang.n_a
                                ptc1C = lang.n_a
                            }
                            if (
                                subsystem.architecture === ARCHITECTURE_1oo1
                                || subsystem.architecture === ARCHITECTURE_2oo2
                            ) {
                                beta = lang.n_a
                            }

                            return (
                                <tr key={subsystem.id}>
                                    <td>{subsystem.name}</td>
                                    <td>{subsystem.architecture}</td>
                                    {
                                        showArchitectureConstraints
                                        && <td>{lang[subsystem.architecturalConstraints]}</td>
                                    }
                                    <td>{ptc0A}</td>
                                    <td>{ptc1A}</td>
                                    <td>{ptc0B}</td>
                                    <td>{ptc1B}</td>
                                    <td>{ptc0C}</td>
                                    <td>{ptc1C}</td>
                                    <td>{beta}</td>
                                    <td>{t0}</td>
                                    <td>{t1}</td>
                                    <td>{t2}</td>
                                </tr>
                            )
                        })
                    }
                </tbody>
            </ResultTable>

            <ResultTableAnnotation>{`${lang.n_a} = ${lang.notApplicable}`}</ResultTableAnnotation>
        </BlockContent>
    )
}


export const VdiVde21802019PdfExport = (
    subsystemList: readonly SilResultSubsystem[],
    showArchitectureConstraints: boolean,
    userSettings: UserSettings,
    lang: LangType,
) => {
    const stack: any[] = []

    subsystemList.forEach((subsystem) => {
        const ptc0A = FormatPercentageString(subsystem.vdivde21802019ptc0A, userSettings, lang)
        const ptc1A = FormatPercentageString(subsystem.vdivde21802019ptc1A, userSettings, lang)
        let ptc0B = FormatPercentageString(subsystem.vdivde21802019ptc0B, userSettings, lang)
        let ptc1B = FormatPercentageString(subsystem.vdivde21802019ptc1B, userSettings, lang)
        let ptc0C = FormatPercentageString(subsystem.vdivde21802019ptc0C, userSettings, lang)
        let ptc1C = FormatPercentageString(subsystem.vdivde21802019ptc1C, userSettings, lang)
        let beta = FormatPercentageString(subsystem.vdivde21802019beta, userSettings, lang)
        const t0 = subsystem.vdivde21802019t0
        const t1 = subsystem.vdivde21802019t1
        const t2 = subsystem.vdivde21802019t2

        if (subsystem.architecture === ARCHITECTURE_1oo1) {
            ptc0B = null
            ptc1B = null
        }
        if (
            subsystem.architecture === ARCHITECTURE_1oo1
            || subsystem.architecture === ARCHITECTURE_1oo2
            || subsystem.architecture === ARCHITECTURE_2oo2
        ) {
            ptc0C = null
            ptc1C = null
        }
        if (
            subsystem.architecture === ARCHITECTURE_1oo1
            || subsystem.architecture === ARCHITECTURE_2oo2
        ) {
            beta = null
        }

        const list: string[] = [
            `${lang.architecture}: ${subsystem.architecture}`,
        ]
        if (showArchitectureConstraints) {
            list.push(`${lang.architecturalConstraints}: ${lang[subsystem.architecturalConstraints]}`)
        }
        list.push(
            `${lang.ptc0A}: ${ptc0A}`,
            `${lang.ptc1A}: ${ptc1A}`,
        )

        if (ptc0B !== null) list.push(`${lang.ptc0B}: ${ptc0B}`)
        if (ptc1B !== null) list.push(`${lang.ptc1B}: ${ptc1B}`)
        if (ptc0C !== null) list.push(`${lang.ptc0C}: ${ptc0C}`)
        if (ptc1C !== null) list.push(`${lang.ptc1C}: ${ptc1C}`)
        if (beta !== null) list.push(`${lang.beta}: ${beta}`)

        list.push(
            `${lang.t0}: ${t0} ${lang.hours}`,
            `${lang.t1}: ${t1} ${lang.hours}`,
            `${lang.t2}: ${t2} ${lang.hours}`,
        )

        stack.push(
            {
                bold: true,
                text: `${lang.subsystem} - ${subsystem.name}`,
                margin: Const.SUBSYSTEM_NAME_MARGIN,
            },
            {
                style: Const.LIST,
                ul: list,
            },
        )
    })

    return stack
}
